import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { fromUnix } from "helpers/dateFormat";

export interface ChartSeries {
    label: string;
    color: string;
    // values aligned 1:1 with the chart's `times`; null = gap (no data in bucket)
    values: (number | null)[];
    // dashed renders the line dim + dashed — use for reference lines (e.g. a limit).
    dashed?: boolean;
}

interface ChartProps {
    title: string;
    times: number[]; // unix seconds
    series: ChartSeries[];
    unit?: string;
    height?: number;
}

const VB_W = 1000; // viewBox width; SVG scales to container via preserveAspectRatio

function niceMax(v: number): number {
    if (v <= 0) return 1;
    const pow = Math.pow(10, Math.floor(Math.log10(v)));
    const n = v / pow;
    const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
    return step * pow;
}

function hhmm(unix: number): string {
    return fromUnix(unix).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// Chart renders one or more series as an interactive inline-SVG line chart with a
// legend, y grid, x-axis endpoints, and a hover guide + tooltip. No chart library.
export default function Chart({ title, times, series, unit = "", height = 130 }: ChartProps) {
    const theme = useTheme();
    const [hover, setHover] = useState<number | null>(null);

    const n = times.length;
    const allValues = series.flatMap((s) => s.values).filter((v): v is number => v !== null);
    const hasData = allValues.length > 0;
    const max = niceMax(hasData ? Math.max(...allValues) : 1);

    const padTop = 6;
    const plotH = height - padTop - 4;
    const x = (i: number) => (n <= 1 ? 0 : (i / (n - 1)) * VB_W);
    const y = (v: number) => padTop + plotH - (v / max) * plotH;

    const pathFor = (values: (number | null)[]) => {
        let d = "";
        let pen = false;
        values.forEach((v, i) => {
            if (v === null) { pen = false; return; }
            d += `${pen ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)} `;
            pen = true;
        });
        return d.trim();
    };

    const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        if (rect.width === 0 || n === 0) return;
        const frac = (e.clientX - rect.left) / rect.width;
        setHover(Math.max(0, Math.min(n - 1, Math.round(frac * (n - 1)))));
    };

    const fmt = (v: number) => `${Math.round(v * 100) / 100}${unit}`;
    const hoverFracPct = hover !== null && n > 1 ? (hover / (n - 1)) * 100 : 0;

    return (
        <Box sx={{ flex: "1 1 320px", minWidth: 300 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">{title}</Typography>
                <Stack direction="row" gap={1.5} flexWrap="wrap">
                    {series.map((s) => (
                        <Stack key={s.label} direction="row" alignItems="center" gap={0.5}>
                            <Box sx={{ width: 10, height: 3, bgcolor: s.color, borderRadius: 1 }} />
                            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                        </Stack>
                    ))}
                </Stack>
            </Stack>

            <Box
                sx={{ position: "relative", mt: 0.5, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "action.hover" }}
                onMouseMove={onMove}
                onMouseLeave={() => setHover(null)}
            >
                {hasData ? (
                    <>
                        <svg viewBox={`0 0 ${VB_W} ${height}`} width="100%" height={height} preserveAspectRatio="none" style={{ display: "block" }}>
                            {/* horizontal grid at 0/50/100% */}
                            {[0, 0.5, 1].map((f) => (
                                <line key={f} x1={0} x2={VB_W} y1={padTop + plotH * f} y2={padTop + plotH * f}
                                    stroke={theme.palette.divider} strokeWidth={1} vectorEffect="non-scaling-stroke" />
                            ))}
                            {series.map((s) => (
                                <path key={s.label} d={pathFor(s.values)} fill="none" stroke={s.color}
                                    strokeWidth={s.dashed ? 1.5 : 2} opacity={s.dashed ? 0.6 : 1}
                                    strokeDasharray={s.dashed ? "6,4" : undefined} vectorEffect="non-scaling-stroke" />
                            ))}
                            {hover !== null && (
                                <>
                                    <line x1={x(hover)} x2={x(hover)} y1={padTop} y2={padTop + plotH}
                                        stroke={theme.palette.text.secondary} strokeWidth={1} strokeDasharray="3,3" vectorEffect="non-scaling-stroke" />
                                    {series.map((s) => {
                                        const v = s.values[hover];
                                        return v === null ? null : (
                                            <circle key={s.label} cx={x(hover)} cy={y(v)} r={3.5} fill={s.color}
                                                stroke={theme.palette.background.paper} strokeWidth={1} vectorEffect="non-scaling-stroke" />
                                        );
                                    })}
                                </>
                            )}
                        </svg>

                        {/* y max + zero labels */}
                        <Typography variant="caption" color="text.secondary" sx={{ position: "absolute", top: 2, left: 4 }}>{fmt(max)}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ position: "absolute", bottom: 2, left: 4 }}>0</Typography>

                        {/* hover tooltip */}
                        {hover !== null && (
                            <Box sx={{
                                position: "absolute", top: 4,
                                left: `${hoverFracPct}%`,
                                transform: hoverFracPct > 60 ? "translateX(-100%)" : "translateX(6px)",
                                bgcolor: "background.paper", border: 1, borderColor: "divider", borderRadius: 1,
                                px: 1, py: 0.5, pointerEvents: "none", zIndex: 2, boxShadow: 2, whiteSpace: "nowrap",
                            }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{hhmm(times[hover])}</Typography>
                                {series.map((s) => (
                                    <Stack key={s.label} direction="row" alignItems="center" gap={0.5}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.color }} />
                                        <Typography variant="caption">
                                            {s.label}: <b>{s.values[hover] === null ? "—" : fmt(s.values[hover] as number)}</b>
                                        </Typography>
                                    </Stack>
                                ))}
                            </Box>
                        )}
                    </>
                ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", py: 3 }}>
                        no data in window
                    </Typography>
                )}
            </Box>

            {hasData && n > 1 && (
                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">{hhmm(times[0])}</Typography>
                    <Typography variant="caption" color="text.secondary">{hhmm(times[n - 1])}</Typography>
                </Stack>
            )}
        </Box>
    );
}
